import React, {MouseEvent, ReactNode} from 'react';
import {observer} from 'mobx-react';
import $ from 'jquery';
import {MemoryBarTab, MemoryItemConversion, MemoryItemConversionType} from '../../stores/MemoryBarStore';
import {classes, stylesheet} from 'typestyle';
import {px} from 'csx';
import onClickOutside from 'react-onclickoutside';
import {I18n, TProvider} from '../../i18n/i18n';
import {Droppable} from 'react-beautiful-dnd';
import {REPORT_DROPABLES} from '../../beautiful-dnd/Droppable';
import {Draggables} from '../../beautiful-dnd/Draggable';
import {CiBlueGradientColor, WarningColor, WhiteColor} from '../../styles/colors';

interface MemoryBarState {
    searchIsVisible: boolean;
    searchText: string;
}

interface MemoryBarProps {
    isOpen: () => boolean;
    activeMemoryTabPackage: {
        get: () => MemoryBarTab;
        set: (memoryBarTab: MemoryBarTab) => void;
    };
    bookmarkItems: ReactNode[];
    historyItems: ReactNode[];
    handleOutsideClick?: () => void;
    memoryItemConversion: {
        get: () => MemoryItemConversion;
        set: (type: MemoryItemConversionType) => void;
    };
    applySearchText: (text: string) => void;
}

@observer
class MemoryBar extends React.Component<MemoryBarProps, MemoryBarState> {
    private searchInput: HTMLInputElement | null;

    constructor(props: MemoryBarProps) {
        super(props);
        this.state = {
            searchIsVisible: false,
            searchText: '',
        };
        this.searchInput = null;
    }

    public handleClickOutside = (e: MouseEvent<HTMLElement>): void => {
        if (this.props.handleOutsideClick) {
            this.props.handleOutsideClick();
        }
    };

    public componentWillReceiveProps() {
        if (this.props.isOpen()) {
            $('.memoryBarScrollContainer').scrollTop(0);
        }
    }

    public componentWillUpdate(nextProps: MemoryBarProps, nextState: MemoryBarState) {
        if (!this.state.searchIsVisible && nextState.searchIsVisible && this.searchInput) {
            this.searchInput.focus();
        }
    }

    public getMemoryBarWidth() {
        return this.props.isOpen() ? 300 : 0;
    }

    public onSinkClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    private searchInputUpdated = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            searchText: event.currentTarget.value,
        });
        this.props.applySearchText(event.currentTarget.value);
    };

    private showSearchInputIfNecessary() {
        const visibilityClass = this.state.searchIsVisible ? 'visible' : '';

        return (
            <div className={classes(barCss.searchInputContainer, visibilityClass)}>
                <TProvider>
                    {(t) => (
                        <input
                            value={this.state.searchText}
                            onChange={this.searchInputUpdated}
                            placeholder={t('search')}
                            ref={(ref) => {
                                this.searchInput = ref;
                            }}
                            type="text"
                            className={barCss.searchInput}
                        />
                    )}
                </TProvider>
            </div>
        );
    }

    private toggleSearchInputVisibility = () => {
        this.setState({
            searchIsVisible: !this.state.searchIsVisible,
            searchText: '',
        });
    };

    public render() {
        let historyTabClass = barCss.tabItem;
        let favoriteTabClass = barCss.tabItem;
        let memoryItems: React.ReactNode[] = [];
        const activeTab = this.props.activeMemoryTabPackage.get();
        const {favoriteItemWasAdded, favoriteItemWasDowngradedToHistoryItem} = this.props.memoryItemConversion.get();

        switch (activeTab) {
            case MemoryBarTab.HISTORY:
                historyTabClass += ' ' + barCss.activeTab;
                memoryItems = this.props.historyItems;
                break;
            case MemoryBarTab.FAVORITES:
                favoriteTabClass += ' ' + barCss.activeTab;
                memoryItems = this.props.bookmarkItems;
                break;
            default:
                break;
        }

        if (favoriteItemWasAdded) {
            favoriteTabClass = classes(favoriteTabClass, barCss.tabItemMarked);
        }

        if (favoriteItemWasDowngradedToHistoryItem) {
            historyTabClass = classes(historyTabClass, barCss.tabItemMarked);
        }

        return (
            <div className={barCss.container} style={{width: this.getMemoryBarWidth()}} onClick={this.onSinkClick}>
                <div className={barCss.tabContainer}>
                    <div onClick={() => this.props.activeMemoryTabPackage.set(MemoryBarTab.HISTORY)} className={historyTabClass}>
                        <I18n i18nKey="historyTabOpenerText" />
                    </div>
                    <div style={{display: 'inline', padding: 10}}>/</div>
                    <div className={favoriteTabClass} onClick={() => this.props.activeMemoryTabPackage.set(MemoryBarTab.FAVORITES)}>
                        <I18n i18nKey="favoriteTabOpenerText" />
                    </div>
                    <div
                        onClick={this.toggleSearchInputVisibility}
                        className={classes(barCss.searchButton, this.state.searchIsVisible ? 'active' : '')}>
                        <i className="fa fa-search" aria-hidden="true" />
                    </div>
                </div>
                {this.showSearchInputIfNecessary()}
                <div className={barCss.containerInner}>
                    <Droppable
                        isDropDisabled={true}
                        droppableId={REPORT_DROPABLES.MEMORY_BAR + '-' + activeTab}
                        type={Draggables.ONLY_DROPPABLE_ON_REPORT_PAPER}>
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} style={{width: 300}}>
                                {memoryItems}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </div>
            </div>
        );
    }
}

export default onClickOutside(MemoryBar);

const barCss = stylesheet({
    tabContainer: {
        height: 58,
        lineHeight: px(58),
        textAlign: 'center',
        color: WhiteColor,
        position: 'relative',
    },
    container: {
        overflow: 'hidden',
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: 1030,
        backgroundColor: CiBlueGradientColor,
        WebkitTransition: '.2s',
        MozTransition: '.2s',
        OTransition: '.2s',
        transition: '.2s',
    },
    containerInner: {
        width: 600,
        overflowY: 'scroll',
        height: '100%',
        paddingBottom: 150,
    },
    tabItemMarked: {
        backgroundColor: WarningColor,
    },
    tabItem: {
        display: 'inline',
        transition: '0.2s',
        padding: 10,
        borderRadius: 2,
        cursor: 'pointer',
    },
    activeTab: {
        background: WhiteColor,
        color: CiBlueGradientColor,
    },
    searchButton: {
        transition: '0.2s',
        position: 'absolute',
        height: 40,
        width: 40,
        borderRadius: '100%',
        top: 10,
        right: 10,
        lineHeight: px(40),
        textAlign: 'center',
        backgroundColor: 'transparent',
        color: WhiteColor,
        $nest: {
            '&.active': {
                backgroundColor: WhiteColor,
                color: CiBlueGradientColor,
            },
        },
    },
    searchInputContainer: {
        display: 'block',
        overflow: 'hidden',
        transition: '0.2s',
        margin: 0,
        height: 0,
        $nest: {
            '&.visible': {
                margin: '0px 5px 5px 5px',
                height: 35,
            },
        },
    },
    searchInput: {
        width: '100%',
        borderRadius: 2,
        border: 'none',
        height: '100%',
        lineHeight: px(25),
        padding: 5,
    },
});



// WEBPACK FOOTER //
// ./src/views/MemoryBar/MemoryBar.tsx